import React, { useCallback, useRef, useState, useMemo, useEffect } from "react";
import { connect } from "react-redux";
import {
    Column,
    DataGrid,
    Selection,
    Export,
    Sorting,
    Paging,
    Pager,
    Toolbar,
    Item,
    Editing,
    FilterRow,
    StateStoring,
    ColumnChooser,
    ColumnChooserSearch,
    Lookup
} from "devextreme-react/data-grid";
import DataSource from "devextreme/data/data_source";
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { LoadPanel } from 'devextreme-react/load-panel';
import data from "../../../../../assets/apiRoutes.json";
import notify from 'devextreme/ui/notify';

const typeOptions = [
    { id: 'R', text: 'Receipt' },
    { id: 'S', text: 'Shipment' },
    { id: 'T', text: 'Storage' },
    { id: 'A', text: 'Adjustment' },
    { id: 'I', text: 'Special Action' },
];

const usageOptions = [
    { id: 'Y', text: 'Always Use' },
    { id: 'M', text: 'Manual Entry' },
    { id: 'T', text: 'Bill Third Party - Automatic Apply' },
];

const rateSourceOptions = [
    { id: 'C', text: 'Charge Code' },
    { id: 'R', text: 'Rate Schedule' },
];

function ChargesTab(props) {
    const accessToken = props?.oidc?.user?.access_token;
    const dateGrid = useRef(null);
    const detailGrid = useRef(null);
    const [loadPanelVisible, setLoadPanelVisible] = useState(false);
    const [selectedItemKeys, setSelectedItemKeys] = useState([]);
    const [selectedDateRowId, setSelectedDateRowId] = useState(null);
    const [selectedDetailKeys, setSelectedDetailKeys] = useState([]);
    const gridKey = "A1B2C3D4-E5F6-7A8B-9C0D-1E2F3A4B5C6D";
    const detailGridKey = "AB2C9167-3144-42D1-92C1-EB1E12D75BF3";

    const position = { of: '#accordion' };

    const chargeCodeLookupStore = useMemo(() => {
        return createStore({
            key: "RowId",
            loadUrl: `${window._env_.REACT_APP_APIS}/${data.ChargeCode.GetChargeCodes}`,
            onBeforeSend: function (method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken,
                };
            }
        });
    }, [accessToken]);

    const billToLookupStore = useMemo(() => {
        return createStore({
            key: "id",
            loadUrl: `${window._env_.REACT_APP_APIS}/id/Lookup/GetCustomers`,
            onBeforeSend: function (method, ajaxOptions) {
                ajaxOptions.headers = {
                    Authorization: `Bearer ${accessToken}`,
                    access_token: accessToken,
                };
            }
        });
    }, [accessToken]);

    const hideLoadPanel = useCallback(() => {
        setLoadPanelVisible(false);
    }, []);

    // ============================================================
    // First Grid: Rate Schedule Dates
    // ============================================================
    const loadState = useCallback(() => {
        return null;
    }, []);

    const saveState = useCallback((state) => {
    }, []);

    const dataSource = useMemo(() => {
        if (!props.headerId || props.headerId === '00000000-0000-0000-0000-000000000000') {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDate.GetRateScheduleDates}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDate.InsertRateScheduleDate}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDate.UpdateRateScheduleDate}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDate.DeleteRateScheduleDate}`,
                loadParams: {
                    rateScheduleHeaderRowId: props.headerId
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };

                    if (method === "insert" || method === "update") {
                        ajaxOptions.headers['Content-Type'] = 'application/json';
                    }

                    if (method === "insert") {
                        try {
                            let valuesObj = {};
                            if (ajaxOptions.data && ajaxOptions.data.values) {
                                if (typeof ajaxOptions.data.values === 'string') {
                                    valuesObj = JSON.parse(ajaxOptions.data.values);
                                } else {
                                    valuesObj = ajaxOptions.data.values;
                                }
                            }

                            const payload = {
                                RowId: '00000000-0000-0000-0000-000000000000',
                                RRateScheduleHeader: props.headerId,
                                EffectiveDate: valuesObj.EffectiveDate || null,
                                Description: valuesObj.Description || null,
                                Comment: valuesObj.Comment || null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing insert values:", e);
                        }
                    }

                    if (method === "update") {
                        try {
                            let valuesObj = {};
                            let key = null;

                            if (ajaxOptions.data) {
                                key = ajaxOptions.data.key;
                                if (ajaxOptions.data.values) {
                                    if (typeof ajaxOptions.data.values === 'string') {
                                        valuesObj = JSON.parse(ajaxOptions.data.values);
                                    } else {
                                        valuesObj = ajaxOptions.data.values;
                                    }
                                }
                            }

                            const payload = {
                                RowId: key,
                                RRateScheduleHeader: props.headerId,
                                EffectiveDate: valuesObj.EffectiveDate !== undefined ? valuesObj.EffectiveDate : null,
                                Description: valuesObj.Description !== undefined ? valuesObj.Description : null,
                                Comment: valuesObj.Comment !== undefined ? valuesObj.Comment : null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing update values:", e);
                        }
                    }
                },
                onInserted: function (e) {
                    notify("Rate Schedule Date inserted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (dateGrid.current?.instance) {
                            dateGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onUpdated: function (key, updatedValues) {
                    notify("Rate Schedule Date updated successfully.", "success", 3000);
                    setTimeout(() => {
                        if (dateGrid.current?.instance) {
                            dateGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onInsertError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to insert Rate Schedule Date.";
                    notify(message, "error", 5000);
                },
                onUpdateError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to update Rate Schedule Date.";
                    notify(message, "error", 5000);
                },
                onRemoved: function (key) {
                    notify("Rate Schedule Date deleted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (dateGrid.current?.instance) {
                            dateGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onRemoveError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to delete Rate Schedule Date.";
                    notify(message, "error", 5000);
                }
            }),
            key: "RowId",
            paginate: false
        });
    }, [props.headerId, accessToken]);

    const onSelectionChanged = useCallback((e) => {
        setSelectedItemKeys(e.selectedRowKeys);
        if (e.selectedRowKeys && e.selectedRowKeys.length > 0) {
            setSelectedDateRowId(e.selectedRowKeys[0]);
        } else {
            setSelectedDateRowId(null);
        }
    }, []);

    const onInitNewRow = useCallback((e) => {
        e.data.RowId = '00000000-0000-0000-0000-000000000000';
        e.data.RRateScheduleHeader = props.headerId;
        e.data.EffectiveDate = null;
        e.data.Description = '';
        e.data.Comment = '';
    }, [props.headerId]);

    const onRowInserted = useCallback((e) => {
        if (dateGrid.current?.instance) {
            dateGrid.current.instance().selectRows([e.key], true);
        }
    }, []);

    const onRowUpdating = useCallback((e) => {
    }, []);

    const onRowUpdated = useCallback(() => {
        if (dateGrid.current?.instance) {
            dateGrid.current.instance().refresh();
        }
    }, []);

    const onEditingStart = useCallback((e) => {
        if (e.key != null && e.key != undefined && !dateGrid.current?.instance()?.isRowSelected(e.key)) {
            dateGrid.current.instance().selectRows([e.key], false);
        }
    }, []);

    const onEditCanceled = useCallback(() => {
    }, []);

    const onContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    }, []);

    // ============================================================
    // Second Grid: Rate Schedule Details (based on selected Date row)
    // ============================================================
    const loadStateDetail = useCallback(() => {
        return null;
    }, []);

    const saveStateDetail = useCallback((state) => {
    }, []);

    const detailDataSource = useMemo(() => {
        if (!selectedDateRowId) {
            return new DataSource({
                store: [],
                key: "RowId"
            });
        }

        return new DataSource({
            store: createStore({
                key: "RowId",
                loadUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDetail.GetRateScheduleDetails}`,
                insertUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDetail.InsertRateScheduleDetail}`,
                updateUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDetail.UpdateRateScheduleDetail}`,
                deleteUrl: `${window._env_.REACT_APP_APIS}/${data.RateScheduleDetail.DeleteRateScheduleDetail}`,
                loadParams: {
                    rateScheduleDateRowId: selectedDateRowId
                },
                onBeforeSend: function (method, ajaxOptions) {
                    ajaxOptions.headers = {
                        Authorization: `Bearer ${accessToken}`,
                        access_token: accessToken,
                    };

                    if (method === "insert" || method === "update") {
                        ajaxOptions.headers['Content-Type'] = 'application/json';
                    }

                    if (method === "insert") {
                        try {
                            let valuesObj = {};
                            if (ajaxOptions.data && ajaxOptions.data.values) {
                                if (typeof ajaxOptions.data.values === 'string') {
                                    valuesObj = JSON.parse(ajaxOptions.data.values);
                                } else {
                                    valuesObj = ajaxOptions.data.values;
                                }
                            }

                            const payload = {
                                RowId: '00000000-0000-0000-0000-000000000000',
                                RRateScheduleHeader: selectedDateRowId,
                                RChargeCode: valuesObj.RChargeCode || null,
                                Usage: valuesObj.Usage || null,
                                RateSource: valuesObj.RateSource || null,
                                UnitsSource: valuesObj.UnitsSource || null,
                                Comment: valuesObj.Comment || null,
                                MinimumAmount: valuesObj.MinimumAmount ?? null,
                                Type: valuesObj.Type || null,
                                Rate: valuesObj.Rate ?? null,
                                RBillTo: valuesObj.RBillTo || null,
                                BeginDay: valuesObj.BeginDay ?? null,
                                EndDay: valuesObj.EndDay ?? null,
                                DeferredRevenuePercent: valuesObj.DeferredRevenuePercent ?? null,
                                SequenceNo: valuesObj.SequenceNo ?? null,
                                Description: valuesObj.Description || null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing insert values:", e);
                        }
                    }

                    if (method === "update") {
                        try {
                            let valuesObj = {};
                            let key = null;

                            if (ajaxOptions.data) {
                                key = ajaxOptions.data.key;
                                if (ajaxOptions.data.values) {
                                    if (typeof ajaxOptions.data.values === 'string') {
                                        valuesObj = JSON.parse(ajaxOptions.data.values);
                                    } else {
                                        valuesObj = ajaxOptions.data.values;
                                    }
                                }
                            }

                            const payload = {
                                RowId: key,
                                RRateScheduleHeader: selectedDateRowId,
                                RChargeCode: valuesObj.RChargeCode !== undefined ? valuesObj.RChargeCode : null,
                                Usage: valuesObj.Usage !== undefined ? valuesObj.Usage : null,
                                RateSource: valuesObj.RateSource !== undefined ? valuesObj.RateSource : null,
                                UnitsSource: valuesObj.UnitsSource !== undefined ? valuesObj.UnitsSource : null,
                                Comment: valuesObj.Comment !== undefined ? valuesObj.Comment : null,
                                MinimumAmount: valuesObj.MinimumAmount !== undefined ? valuesObj.MinimumAmount : null,
                                Type: valuesObj.Type !== undefined ? valuesObj.Type : null,
                                Rate: valuesObj.Rate !== undefined ? valuesObj.Rate : null,
                                RBillTo: valuesObj.RBillTo !== undefined ? valuesObj.RBillTo : null,
                                BeginDay: valuesObj.BeginDay !== undefined ? valuesObj.BeginDay : null,
                                EndDay: valuesObj.EndDay !== undefined ? valuesObj.EndDay : null,
                                DeferredRevenuePercent: valuesObj.DeferredRevenuePercent !== undefined ? valuesObj.DeferredRevenuePercent : null,
                                SequenceNo: valuesObj.SequenceNo !== undefined ? valuesObj.SequenceNo : null,
                                Description: valuesObj.Description !== undefined ? valuesObj.Description : null
                            };
                            ajaxOptions.data = JSON.stringify(payload);
                        } catch (e) {
                            console.error("Error parsing update values:", e);
                        }
                    }
                },
                onInserted: function (e) {
                    notify("Rate Schedule Detail inserted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (detailGrid.current?.instance) {
                            detailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onUpdated: function (key, updatedValues) {
                    notify("Rate Schedule Detail updated successfully.", "success", 3000);
                    setTimeout(() => {
                        if (detailGrid.current?.instance) {
                            detailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onInsertError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to insert Rate Schedule Detail.";
                    notify(message, "error", 5000);
                },
                onUpdateError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to update Rate Schedule Detail.";
                    notify(message, "error", 5000);
                },
                onRemoved: function (key) {
                    notify("Rate Schedule Detail deleted successfully.", "success", 3000);
                    setTimeout(() => {
                        if (detailGrid.current?.instance) {
                            detailGrid.current.instance().refresh();
                        }
                    }, 100);
                },
                onRemoveError: function (error) {
                    const message = error?.message || error?.responseJSON?.message || "Failed to delete Rate Schedule Detail.";
                    notify(message, "error", 5000);
                }
            }),
            key: "RowId",
            paginate: false
        });
    }, [selectedDateRowId, accessToken]);

    const onDetailSelectionChanged = useCallback((e) => {
        setSelectedDetailKeys(e.selectedRowKeys);
    }, []);

    const onDetailInitNewRow = useCallback((e) => {
        e.data.RowId = '00000000-0000-0000-0000-000000000000';
        e.data.RRateScheduleHeader = selectedDateRowId;
        e.data.RChargeCode = null;
        e.data.Usage = '';
        e.data.RateSource = '';
        e.data.UnitsSource = '';
        e.data.Comment = '';
        e.data.MinimumAmount = 0;
        e.data.Type = '';
        e.data.Rate = 0;
        e.data.RBillTo = null;
        e.data.BeginDay = null;
        e.data.EndDay = null;
        e.data.DeferredRevenuePercent = null;
        e.data.SequenceNo = null;
        e.data.Description = '';
    }, [selectedDateRowId]);

    const onDetailRowInserted = useCallback((e) => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance().selectRows([e.key], true);
        }
    }, []);

    const onDetailRowUpdating = useCallback((e) => {
    }, []);

    const onDetailRowUpdated = useCallback(() => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance().refresh();
        }
    }, []);

    const onDetailEditingStart = useCallback((e) => {
        if (e.key != null && e.key != undefined && !detailGrid.current?.instance()?.isRowSelected(e.key)) {
            detailGrid.current.instance().selectRows([e.key], false);
        }
    }, []);

    const onDetailEditCanceled = useCallback(() => {
    }, []);

    const onDetailContentReady = useCallback((e) => {
        if (!e.component._hasLoaded) {
            e.component._hasLoaded = true;
            var selection = e.component.getSelectedRowKeys();
            if (selection.length == 0) {
                e.component.selectRowsByIndexes([0]);
            }
        }
    }, []);

    // Reset the second grid's loaded flag when the selected date changes
    useEffect(() => {
        if (detailGrid.current?.instance) {
            detailGrid.current.instance()._hasLoaded = false;
        }
    }, [selectedDateRowId]);

    return (
        <div>
            <LoadPanel
                shadingColor="rgba(0,0,0,0.4)"
                position={position}
                onHiding={hideLoadPanel}
                visible={loadPanelVisible}
                showIndicator={true}
                shading={true}
                showPane={true}
                hideOnOutsideClick={false}
            />

            {/* First Grid: Rate Schedule Dates */}
            <h6 style={{ margin: '10px 0 5px 0', fontWeight: 'bold' }}>Rate Schedule Dates</h6>
            <DataGrid
                dataSource={dataSource}
                ref={dateGrid}
                selectedRowKeys={selectedItemKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onSelectionChanged}
                onInitNewRow={onInitNewRow}
                onRowInserted={onRowInserted}
                onRowUpdating={onRowUpdating}
                onRowUpdated={onRowUpdated}
                onEditingStart={onEditingStart}
                onEditCanceled={onEditCanceled}
                onContentReady={onContentReady}
                height="35vh"
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadState}
                    storageKey={gridKey}
                    customSave={saveState}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={true}
                    allowUpdating={true}
                    allowDeleting={true}
                    newRowPosition="last"
                    confirmDelete={true}
                    texts={{ confirmDeleteMessage: "Are you sure you want to delete this Rate Schedule Date?" }}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} />
                <Column dataField="RRateScheduleHeader" caption="Header ID" visible={false} />
                <Column
                    dataField="EffectiveDate"
                    caption="Effective Date"
                    dataType="date"
                    format="MM/dd/yyyy"
                    allowSorting={false}
                />
                <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Column
                    dataField="CreationDateTime"
                    caption="Creation Date"
                    dataType="date"
                    format="MM/dd/yyyy HH:mm:ss"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="CreationUserId"
                    caption="Created By"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="LastUpdateDateTime"
                    caption="Last Update Date"
                    dataType="date"
                    format="MM/dd/yyyy HH:mm:ss"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Column
                    dataField="LastUpdateUserId"
                    caption="Last Updated By"
                    allowEditing={false}
                    allowSorting={false}
                    visible={false}
                />
                <Toolbar>
                    <Item name="addRowButton" />
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>

            {/* Second Grid: Rate Schedule Details */}
            <h6 style={{ margin: '15px 0 5px 0', fontWeight: 'bold' }}>Rate Schedule Details</h6>
            <DataGrid
                dataSource={detailDataSource}
                ref={detailGrid}
                selectedRowKeys={selectedDetailKeys}
                showBorders={true}
                rowAlternationEnabled={true}
                allowColumnReordering={true}
                allowColumnResizing={true}
                columnAutoWidth={true}
                onSelectionChanged={onDetailSelectionChanged}
                onInitNewRow={onDetailInitNewRow}
                onRowInserted={onDetailRowInserted}
                onRowUpdating={onDetailRowUpdating}
                onRowUpdated={onDetailRowUpdated}
                onEditingStart={onDetailEditingStart}
                onEditCanceled={onDetailEditCanceled}
                onContentReady={onDetailContentReady}
                height="35vh"
                noDataText={selectedDateRowId ? "No details found" : "Select a Rate Schedule Date row above"}
            >
                <StateStoring
                    enabled={true}
                    type="custom"
                    customLoad={loadStateDetail}
                    storageKey={detailGridKey}
                    customSave={saveStateDetail}
                />
                <ColumnChooser
                    enabled={true}
                    title="Choose Column"
                    width={290}
                    height={290}
                    sortOrder="asc"
                >
                    <ColumnChooserSearch
                        enabled={true}
                        editorOptions={{ placeholder: 'Search column' }} />
                </ColumnChooser>
                <Selection mode="single" />
                <Export enabled={true} allowExportSelectedData={true} />
                <Editing
                    mode="cell"
                    refreshMode="repaint"
                    allowAdding={!!selectedDateRowId}
                    allowUpdating={!!selectedDateRowId}
                    allowDeleting={!!selectedDateRowId}
                    newRowPosition="last"
                    confirmDelete={true}
                    texts={{ confirmDeleteMessage: "Are you sure you want to delete this Rate Schedule Detail?" }}
                />
                <FilterRow visible={true} />
                <Sorting mode="none" />
                <Paging enabled={false} />

                <Column dataField="RowId" caption="Row ID" visible={false} />
                <Column dataField="RRateScheduleHeader" caption="Date ID" visible={false} />
                 <Column
                    dataField="Type"
                    caption="Type"
                    dataType="string"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={typeOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                <Column
                    dataField="RChargeCode"
                    caption="Charge Code"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={chargeCodeLookupStore}
                        valueExpr="RowId"
                        displayExpr="ChargeCode"
                    />
                </Column>
                <Column
                    dataField="ChargeDescription"
                    caption="Charge Description"
                    dataType="string"
                    allowEditing={false}
                    allowSorting={false}
                />
                 <Column
                    dataField="Description"
                    caption="Description"
                    dataType="string"
                    allowSorting={false}
                    allowEditing={false}
                />
                 <Column
                    dataField="Usage"
                    caption="Usage"
                    dataType="string"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={usageOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                <Column
                    dataField="RateSource"
                    caption="Rate Source"
                    dataType="string"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={rateSourceOptions}
                        valueExpr="id"
                        displayExpr="text"
                    />
                </Column>
                <Column
                    dataField="Rate"
                    caption="Rate"
                    dataType="number"
                    format="#,##0.00"
                    allowSorting={false}
                />
                <Column
                    dataField="MinimumAmount"
                    caption="Minimum Amount"
                    dataType="number"
                    format="#,##0.00"
                    allowSorting={false}
                />
                <Column
                    dataField="RBillTo"
                    caption="Bill To"
                    allowSorting={false}
                >
                    <Lookup
                        dataSource={billToLookupStore}
                        valueExpr="id"
                        displayExpr="name"
                    />
                </Column>
                <Column
                    dataField="BeginDay"
                    caption="Begin Day"
                    dataType="number"
                    allowSorting={false}
                />
                <Column
                    dataField="EndDay"
                    caption="End Day"
                    dataType="number"
                    allowSorting={false}
                />
                <Column
                    dataField="DeferredRevenuePercent"
                    caption="Deferred Revenue %"
                    dataType="number"
                    format="#,##0.00"
                    allowSorting={false}
                />
                <Column
                    dataField="Comment"
                    caption="Comment"
                    dataType="string"
                    allowSorting={false}
                />
                <Toolbar>
                    <Item name="addRowButton" />
                    <Item name="saveButton" />
                    <Item name="revertButton" />
                    <Item name="exportButton" />
                    <Item name="columnChooserButton" />
                </Toolbar>
            </DataGrid>
        </div>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc
    };
}

export default connect(mapStateToProps)(ChargesTab);
